// uiTrack.ts · v12.10.13 (#1601 T87 Click Analyzer) · 2026-10-10 · VoiceSpectrum AI
// UI usage for Support Ops › Click Analyzer: which named controls of Messages and the message drawer are used, and which
// SCREENS of the practice app are opened and for how long. Results are shown BY ROLE in Support Ops, never by person.
//
// PERFORMANCE RULE (owner 10/10: "not at the cost of performance where user feel the system is sluggish")
//   * a click costs one closest() lookup and one array push - nothing else runs on the click path;
//   * events are held in memory and sent in small batches (max 200) about every 30 s, when the browser is idle
//     (requestIdleCallback), and when the page is hidden / closed (fetch keepalive - sendBeacon cannot carry the sign-in
//     token, so keepalive fetch is the "beacon"); never one network call per click; nothing is sent when nothing happened;
//   * errors are dropped silently; the server answers { on:false } when the practice's "UI usage" switch is off (or for a
//     VoiceSpectrum Support visit) and this page then stops collecting until it is reloaded.
//
// NEVER RECORDED: patient names, phone numbers, message / call content, typed or search text, record ids. Names are fixed
// words chosen by the developer; values only from FIXED lists (the server drops anything else again - UiUsageRules).
//
// ── THE data-track CONTRACT (the server's UiUsageCatalog reads the same names - change both together) ────────────────
//   data-track="<name>"          on a button / link / row: a click anywhere inside it records <name>.
//                                <name> = lower-case a-z 0-9 . _ - (max 64), NEVER an id, a name or a number.
//   data-track-group="<group>"   optional, a-z 0-9 _ - (filters: the filter group key).
//   data-track-value="<value>"   optional, ONLY a fixed-list word (calls, missed, voicemails, texts, read, unread, normal,
//                                urgent, success, watch, failure, pending, completed, with, without, newest, oldest, today,
//                                yesterday, 7d, 15d, 30d, custom, en, es ...) or a 1-2 digit count. Staff names, labels,
//                                categories, callers: leave the value OFF - the group being used is what is recorded.
//   data-track-screen="<screen>" on a TAB that changes what the screen is (Reports: reports-analyze | reports-call-record).
//   For a <select> or a text box (no click), call track('<name>', { value }) in onChange - never pass what was typed.
//
//   Messages      messages.row.open · messages.row.arrow · messages.filters.open · messages.search (once per search, never
//                 the words) · messages.pill.me · messages.pill.reminders · messages.pill.urgent · messages.date.open ·
//                 messages.date.pick (value today|yesterday|7d|15d|30d|custom) · messages.sort (value newest|oldest) ·
//                 messages.tab.calls · messages.tab.texts · messages.dayjump · messages.empty.reset
//   Filters panel filters.group.<group> - ONCE per Show results for each group that changed, group = status | type | opened |
//                 priority | category | label | assigned | caller | outcome | notes | language, value = the fixed-list
//                 value when the group is a fixed list (Read/Unread, Calls/Missed/Voicemails, Normal/Urgent, Success/Watch/
//                 Failure, Pending/Completed, With/Without notes), otherwise no value ·
//                 filters.show (value = how many filters are on, "0".."99") · filters.reset · filters.showall · filters.listsearch
//                 (closed without Show results = filter opens - filters.show; no event needed)
//   Drawer        trackOverlay('drawer') when the drawer opens - call the returned stop() when it closes (time in a drawer) ·
//                 drawer.section.<needs|es|classic|recording|transcript|calldata|callpath|print> (opened / used) ·
//                 drawer.action.<complete|callback|note|assign|reminder|text|label|unread|next|prev|flag|close> ·
//                 drawer.scroll.<needs|recording|transcript|end> - ONCE per drawer when that depth is first reached.
//   Screens       automatic (Shell): messages · followups · contacts · reports-analyze · reports-call-record ·
//                 settings (group = the section) · help · workflow-designer · other. A view counts when on screen >= 2 s.
import { buildUrl } from './api';

export interface TrackEvent { t: 'click' | 'view'; name: string; group?: string; value?: string; ms?: number; at: number }
type Props = { group?: string; value?: string };

const MAX_BATCH = 200;
const MAX_BUFFER = 1000;
const FLUSH_EVERY_MS = 30_000;
export const SCREEN_MIN_MS = 2000;
const OVERLAY_MIN_MS = 300;
const ENDPOINT = '/api/t2/ui/events';

let buf: TrackEvent[] = [];
let off = false;
let installed = false;
let sending = false;

// ── pure helpers (exported for tests) ────────────────────────────────────────────────────────────────────────────
const NAME_RX = /^[a-z0-9._-]{1,64}$/;
const GROUP_RX = /^[a-z0-9_-]{1,32}$/;
const VALUE_RX = /^[a-z0-9-]{1,16}$/;
const DIGITS4 = /\d{4,}/;
/** The FIXED-list values that may leave the browser (the server's UiUsageRules.AllowedValues - change both together). */
export const FIXED_VALUES = new Set([
  'calls', 'missed', 'voicemails', 'texts', 'sms', 'inbound', 'outbound', 'answered',
  'read', 'unread', 'normal', 'urgent', 'success', 'watch', 'failure', 'pending', 'completed', 'open', 'closed', 'in-progress', 'done',
  'with', 'without', 'newest', 'oldest', 'me', 'all', 'on', 'off',
  'today', 'yesterday', '7d', '15d', '30d', 'custom', 'range', 'before', 'after', 'overdue', 'week', 'future',
  'en', 'es',
]);

/** Lower-case, trim, clip to 64; null when not a valid name or it could hold an id / phone number. */
export function sanitizeName(raw: string | null | undefined): string | null {
  const s = (raw ?? '').trim().toLowerCase().slice(0, 64);
  return NAME_RX.test(s) && !DIGITS4.test(s) ? s : null;
}

/** One event as it will be sent - or null (dropped). The server checks everything again. */
export function sanitize(e: TrackEvent): TrackEvent | null {
  const name = sanitizeName(e.name);
  if (!name || (e.t !== 'click' && e.t !== 'view')) return null;
  const out: TrackEvent = { t: e.t, name, at: Math.round(e.at) };
  const g = (e.group ?? '').trim().toLowerCase().slice(0, 32);
  if (g && GROUP_RX.test(g) && !DIGITS4.test(g)) out.group = g;
  const v = (e.value ?? '').trim().toLowerCase();
  if (v && VALUE_RX.test(v) && (FIXED_VALUES.has(v) || /^\d{1,2}$/.test(v))) out.value = v;   // anything else: the group was used, no value
  if (e.t === 'view') {
    const ms = Math.round(e.ms ?? 0);
    if (!(ms > 0)) return null;
    out.ms = Math.min(ms, 4 * 3600 * 1000);
  }
  return out;
}

const SETTINGS_SECTION_RX = /^[a-z0-9-]{1,32}$/;
/** The screen a route is, without ids: /messages -> messages (followups when the Follow-ups view is on), /configuration/ai-agent
 *  -> settings + group ai-agent, /workflows/123 -> workflow-designer. Null = not a screen (sign-in, the redirecting home). */
export function screenFromPath(pathname: string, search = '', opts?: { followups?: boolean }): { name: string; group?: string } | null {
  const p = (pathname || '/').toLowerCase().replace(/\/+$/, '') || '/';
  const parts = p.split('/').filter(Boolean);
  const q = new URLSearchParams(search || '');
  const first = parts[0] ?? '';
  switch (first) {
    case '': return null;
    case 'messages': return { name: opts?.followups || q.get('filter') === 'follow-up' ? 'followups' : 'messages' };
    case 'contacts': return { name: 'contacts' };
    case 'reports': return { name: 'reports-analyze' };
    case 'schedule': return { name: 'schedule' };
    case 'help': return { name: 'help' };
    case 'settings':
    case 'configuration': {
      const sec = (parts[1] ?? '').toLowerCase();
      const page = (q.get('page') ?? '').toLowerCase();
      let group = SETTINGS_SECTION_RX.test(sec) && !DIGITS4.test(sec) ? sec : undefined;
      if (group === 'security' && ['platform', 'users', 'groups'].includes(page)) group = `security-${page}`;
      return group ? { name: 'settings', group } : { name: 'settings' };
    }
    case 'workflows': return parts[1] === 'modal' ? { name: 'workflow-modal' } : { name: 'workflow-designer' };
    case 'login': case 'first-login': case 'reset-password': case 'invite': case 'setup-mfa': case 'support-entry': case 'welcome':
      return null;
    default: return { name: 'other' };
  }
}

// ── recording (the click path: one push) ─────────────────────────────────────────────────────────────────────────
function push(e: TrackEvent): void {
  if (off) return;
  buf.push(e);
  if (buf.length > MAX_BUFFER) buf.splice(0, buf.length - MAX_BUFFER);
  if (buf.length >= MAX_BATCH) scheduleIdle();
}

/** Record that a named control was used. Never pass typed text, names or ids. */
export function track(name: string, props?: Props): void {
  push({ t: 'click', name, group: props?.group, value: props?.value, at: Date.now() });
}

// ── timed spans: the current screen + open overlays ──────────────────────────────────────────────────────────────
interface Span { name: string; group?: string; start: number; min: number }
let screen: Span | null = null;
const overlays = new Map<number, Span>();
let overlaySeq = 0;
let hiddenScreen: Omit<Span, 'start'> | null = null;
const hiddenOverlays = new Map<number, Omit<Span, 'start'>>();

function close(s: Span, now: number): void {
  const ms = now - s.start;
  if (ms >= s.min) push({ t: 'view', name: s.name, group: s.group, ms: Math.round(ms), at: Date.now() });
}

/** The screen on show. Records the previous one (with its time) when it was on screen >= 2 s. Same screen = no-op. */
export function trackScreen(name: string, group?: string): void {
  if (off) return;
  const now = performance.now();
  if (screen && screen.name === name && screen.group === group) return;
  if (screen) close(screen, now);
  screen = { name, group, start: now, min: SCREEN_MIN_MS };
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { hiddenScreen = { name, group, min: SCREEN_MIN_MS }; screen = null; }
}

/** An overlay (the message drawer) opened: returns stop(), call it when it closes. Records overlay.<name> with its time. */
export function trackOverlay(name: string): () => void {
  if (off) return () => { /* off */ };
  const id = ++overlaySeq;
  overlays.set(id, { name: `overlay.${name}`, start: performance.now(), min: OVERLAY_MIN_MS });
  return () => {
    const s = overlays.get(id);
    if (s) { overlays.delete(id); close(s, performance.now()); }
    hiddenOverlays.delete(id);
  };
}

// ── sending ──────────────────────────────────────────────────────────────────────────────────────────────────────
function token(): string | null { try { return localStorage.getItem('vs_t2_token'); } catch { return null; } }

/** Send one batch. keepalive = the page is going away. */
export function flush(keepalive = false): void {
  if (off || buf.length === 0 || (sending && !keepalive)) return;
  const tk = token();
  if (!tk) { buf = []; return; }   // signed out - nothing to send it as
  const events = buf.splice(0, MAX_BATCH).map(sanitize).filter((e): e is TrackEvent => e !== null);
  if (events.length === 0) return;
  sending = true;
  try {
    fetch(buildUrl(ENDPOINT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ events }),
      credentials: 'include',
      keepalive,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { on?: boolean } | null) => { if (j && j.on === false) stop(); })
      .catch(() => { /* dropped silently */ })
      .finally(() => { sending = false; });
  } catch { sending = false; }
}

function stop(): void { off = true; buf = []; screen = null; overlays.clear(); hiddenScreen = null; hiddenOverlays.clear(); }

let idlePending = false;
function scheduleIdle(): void {
  if (idlePending || off) return;
  idlePending = true;
  const run = () => { idlePending = false; flush(false); };
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(run, { timeout: 5000 });
  else setTimeout(run, 1000);
}

function onHide(): void {
  const now = performance.now();
  if (screen) { close(screen, now); hiddenScreen = { name: screen.name, group: screen.group, min: screen.min }; screen = null; }
  overlays.forEach((s, id) => { close(s, now); hiddenOverlays.set(id, { name: s.name, group: s.group, min: s.min }); });
  overlays.clear();
  for (let i = 0; i < 3 && buf.length > 0; i++) flush(true);
}
function onShow(): void {
  const now = performance.now();
  if (hiddenScreen && !screen) screen = { ...hiddenScreen, start: now };
  hiddenScreen = null;
  hiddenOverlays.forEach((s, id) => overlays.set(id, { ...s, start: now }));
  hiddenOverlays.clear();
}

function onClick(e: Event): void {
  if (off) return;
  const t = e.target as Element | null;
  const el = t && typeof t.closest === 'function' ? t.closest('[data-track],[data-track-screen]') : null;
  if (!el) return;
  const scr = el.getAttribute('data-track-screen');
  if (scr) { const s = sanitizeName(scr); if (s) trackScreen(s); }
  const name = el.getAttribute('data-track');
  if (name) push({ t: 'click', name, group: el.getAttribute('data-track-group') ?? undefined, value: el.getAttribute('data-track-value') ?? undefined, at: Date.now() });
}

/** Install once (Shell): the delegated click listener (capture, passive), the 30 s idle flush, and the page-hide flush. */
export function installUiTracking(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  try { if (sessionStorage.getItem('vs.supportMode') === '1') { off = true; return; } } catch { /* keep on */ }
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') onHide(); else onShow(); });
  window.addEventListener('pagehide', onHide);
  window.setInterval(() => { if (buf.length > 0) scheduleIdle(); }, FLUSH_EVERY_MS);
}

/** For tests: what is waiting to be sent, and a reset. */
export function _pending(): TrackEvent[] { return buf.slice(); }
export function _reset(): void { buf = []; off = false; screen = null; overlays.clear(); hiddenScreen = null; hiddenOverlays.clear(); }
